import { Heart, Instagram, ChevronRight, Music2, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

function SpotifyModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={onClose}
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-zinc-900 p-8 rounded-lg w-full max-w-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            <motion.button 
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-zinc-800 rounded-full"
            >
              <X size={20} />
            </motion.button>
            <iframe 
              style={{ borderRadius: '12px' }}
              src="https://open.spotify.com/embed/artist/5m1cLmgZIfEYPLejhLFR10?utm_source=generator" 
              width="100%" 
              height="352" 
              frameBorder="0" 
              allowFullScreen
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
              loading="lazy"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function AboutModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-zinc-900 rounded-lg w-full max-w-5xl relative flex flex-col max-h-[90vh]"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-8 pb-4">
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-zinc-800 rounded-full"
              >
                <X size={20} />
              </motion.button>
              <h2 className="text-2xl font-bold mb-4">About Fresku</h2>
              <p className="text-gray-300">
                Fresku is a Dutch rapper, actor, and theater maker known for his unique style and powerful performances.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ArtistHeader() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [opacity, setOpacity] = useState(1);
  const [blur, setBlur] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const opacityThreshold = 60;
      const blurThreshold = 90;
      
      const newOpacity = scrollPosition <= opacityThreshold 
        ? 1 
        : Math.max(0.5, 1 - ((scrollPosition - opacityThreshold) / opacityThreshold * 0.8));

      const blur = scrollPosition <= opacityThreshold
        ? 0
        : Math.min(6, ((scrollPosition - opacityThreshold) / (blurThreshold - opacityThreshold)) * 6);

      setOpacity(newOpacity);
      setBlur(blur);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div 
      layout
      className="relative h-[40vh] overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        layout
        className="absolute w-full h-[40vh] top-0 left-0"
        style={{ 
          opacity,
          filter: `blur(${blur}px)`
        }}
      >
        <video
          className="absolute w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          style={{ transform: 'translate3d(0, 0, 0)' }}
        >
          <source src="https://cdn.shopify.com/videos/c/o/v/106ea231846a4e608f088173b0646a87.mp4" type="video/mp4" />
        </video>
        <motion.div 
          layout
          className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black" 
        />
      </motion.div>
      
      <motion.div 
        layout
        className="absolute bottom-4 left-4 z-10"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <motion.h1 
          layout
          className="text-4xl font-bold mb-2"
        >
          Fresku
        </motion.h1>
        <motion.p 
          layout
          className="text-gray-200 mb-4"
        >
          Rapper / acteur / theatermaker
        </motion.p>
        <motion.div 
          layout
          className="flex gap-3"
        >
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors cursor-pointer"
          >
            <Music2 size={20} />
            <span className="text-sm">Listen</span>
          </motion.button>
          <motion.a 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            href="https://www.instagram.com/fresku040/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors cursor-pointer"
          >
            <Instagram size={20} />
            <span className="text-sm">Follow</span>
          </motion.a>
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsAboutOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors cursor-pointer"
          >
            <Heart size={20} />
            <span className="text-sm">About</span>
          </motion.button>
        </motion.div>
      </motion.div>

      <SpotifyModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
      <AboutModal 
        isOpen={isAboutOpen} 
        onClose={() => setIsAboutOpen(false)} 
      />
    </motion.div>
  );
}
